import { Link } from "react-router-dom";
import { MindConnectMark, MindConnectWordmark, MindConnectTagline } from "../components/ui/MindConnectLogo";

// Two audiences land on this same login page from two different marketing
// sites (mindconnect.cloud's training pitch, no longer live vs. mindconnect.in's practice
// pitch) - see Login.js's resolveLoginVariant for how `variant` is decided.
const VARIANT_CONTENT = {
  training: {
    tagline: "Your trusted space for psychological care and emotional wellbeing.",
    bullets: [
      { emoji: "📝", title: "Standardised Review Process", text: "Review therapy sessions with clinical precision, standardise evaluations, and significantly boost supervisor efficiency." },
      { emoji: "🧑‍⚕️", title: "Near Real Patient", text: "Connect with patient who simulates real world patient and helps you in solving real world problem." },
      { emoji: "💬", title: "Confidential Sessions", text: "Your conversations remain 100% private and secure." },
      { emoji: "🕊️", title: "Stress & Anxiety Care", text: "Learn coping mechanisms to manage stress, anxiety, and emotional challenges." },
      { emoji: "🌱", title: "Personal Growth", text: "Explore self-awareness, resilience, and emotional intelligence through guided therapy." },
    ],
  },
  practice: {
    tagline: "Run your practice, not just your calendar.",
    bullets: [
      { emoji: "🗓️", title: "Smart Scheduling", text: "Manage appointments across every branch from one calendar, with slots that respect your real working hours." },
      { emoji: "💳", title: "Online & At-Clinic Payments", text: "Accept Razorpay or pay-at-clinic bookings side by side, with earnings reconciled automatically." },
      { emoji: "📋", title: "Patient Records", text: "Consultation history, prescriptions, and reports in one place, with a full audit trail on every edit." },
      { emoji: "💰", title: "Earnings & Payouts", text: "See exactly what you earn per appointment, broken out from gateway fees and tax." },
      { emoji: "👥", title: "Role-Based Staff Access", text: "Give branch admins and receptionists exactly the access they need, across every branch you run." },
    ],
  },
};

export default function AuthLayout({ children, variant = "training" }) {
  const content = VARIANT_CONTENT[variant] || VARIANT_CONTENT.training;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Mobile Header - Logo and Title */}
      <div className="lg:hidden flex items-center justify-center py-6 bg-white border-b border-secondary-200">
        <div className="flex items-center gap-3">
          <MindConnectMark className="w-9 h-9" />
          <div className="leading-tight">
            <MindConnectWordmark className="text-2xl" />
            <MindConnectTagline className="text-[10px]" />
          </div>
        </div>
      </div>

      {/* Left side - Login/Register Form */}
      <div className="flex-1 flex items-center justify-center bg-white">
        <div className="w-full max-w-md p-8">{children}</div>
      </div>

      {/* Right side - App Info */}
      <div className="hidden lg:flex flex-1 flex-col justify-center bg-gradient-to-br from-primary-50 to-accent-50 p-12">

        <div className="flex items-center gap-3 mb-4">
          <MindConnectMark className="w-10 h-10" />
          <div className="leading-tight">
            <MindConnectWordmark className="text-3xl" />
            <MindConnectTagline className="text-xs" />
          </div>
        </div>
        <p className="text-secondary-600 mb-8 text-lg">
          {content.tagline}
        </p>

        <ul className="space-y-6 text-secondary-800 text-base leading-relaxed">
          {content.bullets.map((bullet) => (
            <li key={bullet.title}>
              {bullet.emoji} <span className="font-semibold">{bullet.title}</span> –{" "}
              {bullet.text}
            </li>
          ))}
        </ul>

        <p className="text-xs text-secondary-500 mt-10">
          By continuing, you agree to our{" "}
          <Link to="/terms" className="text-primary-600 cursor-pointer hover:underline">
            Terms & Conditions
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-primary-600 cursor-pointer hover:underline">
            Privacy Policy
          </Link>.
        </p>
      </div>
    </div>
  );
}
